// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useMemo, useRef, type KeyboardEvent } from 'react';

import { useCanEdit } from '../../../app/CaseRoleContext';
import type { CaseActions, CaseData } from '../../../app/bygning/useCaseData';
import { BUILDUPS } from '../../../bygning/buildups';
import {
  galleryDescription,
  galleryEnvPill,
  galleryPartPlaceholder,
  galleryPlaceholder,
  gridMove,
  partCountText,
} from '../../../bygning/galleri';
import { lensToken } from '../../../bygning/lens';
import { activeFilterCount, roomFigure, type FilteredResources } from '../../../bygning/resourceFilter';
import { reviewPill } from '../../../bygning/review';
import { roomsOf } from '../../../bygning/rooms';
import { NO_FILTER_PATCH, type Selection } from '../../../bygning/selection';
import { resourceIndex } from '../../../kortlaegning/resources';
import { rowThumbView, shownView } from '../../../kortlaegning/view';
import { TextLink } from '../../TextLink';
import { ResourceFilters } from '../ResourceFilters';
import { TableViewSwitch } from '../TableViewSwitch';
import { useStoreys } from '../settings/SettingsContext';
import toolbar from '../toolbar.module.css';
import { GalleryCard } from './GalleryCard';
import { GalleryPartCard } from './GalleryPartCard';
import styles from './Galleri.module.css';

export interface GalleriTileProps {
  data: CaseData;
  sel: Selection;
  setSel: (patch: Partial<Selection>) => void;
  actions: CaseActions;
  /** The filtered set (`filterResources`) the cards show, the same the Model's strip and Tabel list. */
  filtered: FilteredResources;
  /** "+ Tilføj uden geometri" (RessourcerMode's `addManual`: create, select, rename). */
  onAddManual: () => void;
}

/**
 * Ressourcer's Galleri (`?view=galleri`): the types as cards — the resource
 * image on top — under Tabel's toolbar (`toolbar.module.css`) and the shared
 * filters (`ResourceFilters`: room, search, miljøstatus, Vigtige,
 * Fejldetektion). Under a room focus a card's figures are the room's share
 * (`roomFigure`). A click selects the type for the inspector, a double-click
 * on an image opens that part's Foto evidence. The arrow keys walk the grid
 * (and select, as Tabel's ↑/↓ do); J/K stay the shell's. No multiselect: the
 * PartBar still floats while parts are picked.
 *
 * Typer | Dele (`?tview=`, shared with Tabel): under Dele the cards are Tabel ›
 * Dele's rows (the set's parts) — each part's own image, a click focuses the
 * part — with its count, and with Fejldetektion its parts marked as an error
 * last, muted.
 */
export function GalleriTile({ data, sel, setSel, filtered, onAddManual }: GalleriTileProps) {
  const canEdit = useCanEdit();
  const gridRef = useRef<HTMLDivElement>(null);
  const index = useMemo(() => resourceIndex(data.resources), [data.resources]);
  const library = data.buildups ?? BUILDUPS;
  const storeys = useStoreys();
  const rooms = useMemo(() => roomsOf(data.types, data.summary), [data.types, data.summary]);

  const dele = sel.tview === 'dele';

  const rows = useMemo(() => filtered.types.map((m) => m.type), [filtered.types]);
  const tabStop = rows.some((t) => t.id === sel.typeId) ? sel.typeId : (rows[0]?.id ?? null);

  // Dele: Tabel › Dele's rows as cards.
  const parts = filtered.parts;
  const dismissedParts = filtered.dismissed;
  const partStop = parts.some((r) => r.part.code === sel.partCode) ? sel.partCode : (parts[0]?.part.code ?? null);

  // The selected card stays in view (J/K, the strip of another view, a ⌘K jump).
  const selector = dele
    ? sel.partCode === null
      ? null
      : `[data-part-code="${CSS.escape(sel.partCode)}"]`
    : sel.typeId === null
      ? null
      : `[data-type-id="${sel.typeId}"]`;
  useEffect(() => {
    if (selector === null) return;
    gridRef.current?.querySelector<HTMLElement>(selector)?.scrollIntoView({ block: 'nearest' });
  }, [selector]);

  // An arrow key moves the focus with the selection, once the card has rendered selected.
  const pendingFocus = useRef<string | null>(null);
  useEffect(() => {
    const target = pendingFocus.current;
    if (target === null) return;
    const el = gridRef.current?.querySelector<HTMLElement>(target);
    if (!el) return;
    pendingFocus.current = null;
    el.focus();
  });

  function onGridKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const card = (e.target as HTMLElement).closest<HTMLElement>('[data-index]');
    if (!card) return;
    const at = Number(card.dataset.index);
    if (!Number.isInteger(at)) return;
    const grid = gridRef.current;
    const columns = grid ? getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length : 1;
    const to = gridMove(at, e.key, dele ? parts.length : rows.length, columns);
    if (to === null) return;
    // Ours, not the shell's (←/→ would otherwise step the strip).
    e.preventDefault();
    e.stopPropagation();
    if (dele) {
      const target = parts[to];
      if (!target || target.part.code === sel.partCode) return;
      pendingFocus.current = `[data-part-code="${CSS.escape(target.part.code)}"]`;
      setSel({ typeId: target.type.id, partCode: target.part.code, selParts: sel.selParts });
      return;
    }
    const target = rows[to];
    if (!target || target.id === sel.typeId) return;
    pendingFocus.current = `[data-type-id="${target.id}"]`;
    setSel({ typeId: target.id, partCode: null });
  }

  const switcher = (
    <>
      <TableViewSwitch label="Galleri" value={sel.tview} onChange={(tview) => setSel({ tview })} />
      <ResourceFilters sel={sel} setSel={setSel} rooms={rooms} storeys={storeys} />
    </>
  );

  // "Ingen … matcher filtrene", with the way back when a filter is set.
  const noMatch = (what: string) => (
    <p className={styles.empty}>
      Ingen {what} matcher filtrene.{' '}
      {activeFilterCount(sel) > 0 && <TextLink onClick={() => setSel(NO_FILTER_PATCH)}>Nulstil filtre</TextLink>}
    </p>
  );

  if (dele) {
    return (
      <div className={styles.tile}>
        <div className={toolbar.tools}>
          <div className={toolbar.toolsLead}>{switcher}</div>
          <div className={toolbar.toolsEnd}>
            <span className={toolbar.count} aria-live="polite">
              {partCountText(parts.length, dismissedParts.length)}
            </span>
          </div>
        </div>

        <div className={styles.wrap}>
          {parts.length === 0 && dismissedParts.length === 0 ? (
            data.types.length === 0 ? (
              <p className={styles.empty}>Ingen dele endnu.</p>
            ) : (
              noMatch('dele')
            )
          ) : (
            <div ref={gridRef} className={styles.grid} role="group" aria-label="Dele" onKeyDown={onGridKey}>
              {parts.map(({ part, type }, i) => {
                const view = rowThumbView([part], index);
                return (
                  <GalleryPartCard
                    key={part.code}
                    part={part}
                    type={type}
                    index={i}
                    selected={part.code === sel.partCode}
                    tabbable={part.code === partStop}
                    swatch={lensToken(type, sel.lens)}
                    view={view}
                    placeholder={galleryPartPlaceholder(part, type)}
                    review={reviewPill(type)}
                    onSelect={() => setSel({ typeId: type.id, partCode: part.code, selParts: sel.selParts })}
                    onOpen={view !== null ? () => setSel({ typeId: type.id, partCode: part.code, selParts: sel.selParts, ev: 'foto' }) : undefined}
                  />
                );
              })}
              {dismissedParts.map(({ part, type }) => (
                <GalleryPartCard
                  key={part.code}
                  part={part}
                  type={type}
                  index={null}
                  selected={false}
                  tabbable={false}
                  swatch={lensToken(type, sel.lens)}
                  view={rowThumbView([part], index)}
                  placeholder={galleryPartPlaceholder(part, type)}
                  review={reviewPill(type)}
                  dismissed
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const count = rows.length === 1 ? '1 type' : `${rows.length} typer`;

  return (
    <div className={styles.tile}>
      <div className={toolbar.tools}>
        <div className={toolbar.toolsLead}>{switcher}</div>
        <div className={toolbar.toolsEnd}>
          <span className={toolbar.count} aria-live="polite">
            {count}
          </span>
        </div>
      </div>

      <div className={styles.wrap}>
        {rows.length === 0 && (data.types.length === 0 ? <p className={styles.empty}>Ingen typer endnu.</p> : noMatch('typer'))}
        {(rows.length > 0 || canEdit) && (
          <div ref={gridRef} className={styles.grid} role="group" aria-label="Typer" onKeyDown={onGridKey}>
            {rows.map((t, i) => {
              const shown = shownView(t, t.id === sel.typeId ? sel.partCode : null, index);
              const code = shown.code;
              return (
                <GalleryCard
                  key={t.id}
                  type={t}
                  index={i}
                  selected={t.id === sel.typeId}
                  tabbable={t.id === tabStop}
                  swatch={lensToken(t, sel.lens)}
                  view={shown.view}
                  placeholder={galleryPlaceholder(t)}
                  description={galleryDescription(t, data.resourceKeys, library, index, roomFigure(t, sel.room))}
                  env={galleryEnvPill(t, data.samples)}
                  review={reviewPill(t)}
                  onSelect={() => setSel({ typeId: t.id })}
                  onOpen={shown.view !== null && code !== null ? () => setSel({ typeId: t.id, partCode: code, ev: 'foto' }) : undefined}
                />
              );
            })}
            {canEdit && (
              <button type="button" className={styles.add} disabled={data.busy} onClick={onAddManual}>
                + Tilføj uden geometri
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
